import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  InputAdornment,
  Box,
  Card,
  CardContent,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import EmailIcon from "@material-ui/icons/Email";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import SendIcon from "@material-ui/icons/Send";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  paper: { overflowX: "auto" },
  logoPreview: {
    width: 40,
    height: 40,
    objectFit: "contain",
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
  },
  uploadSection: {
    marginTop: theme.spacing(1),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  uploadPreview: {
    width: 80,
    height: 60,
    objectFit: "contain",
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    flexShrink: 0,
  },
  fieldSection: { marginBottom: theme.spacing(2) },
  fieldLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 4,
    fontWeight: 500,
  },
  smtpCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  smtpCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  smtpStatusOk: { color: theme.palette.success?.main || "#4caf50", display: "flex", alignItems: "center", gap: 4, fontSize: 12 },
  smtpStatusErr: { color: theme.palette.error.main, display: "flex", alignItems: "center", gap: 4, fontSize: 12 },
  smtpRow: { display: "flex", gap: theme.spacing(2), marginBottom: theme.spacing(2) },
  testResult: {
    padding: theme.spacing(1.5),
    borderRadius: 6,
    marginTop: theme.spacing(1),
    fontSize: 13,
  },
  testOk: { background: "#e8f5e9", color: "#2e7d32" },
  testErr: { background: "#ffebee", color: "#c62828" },
}));

const EMPTY_FORM = {
  domain: "",
  companyId: "",
  type: "company",
  name: "",
  logoUrl: "",
  logoUrlDark: "",
  chatEmptyImageUrl: "",
  chatEmptyImageUrlDark: "",
  loginLogoUrl: "",
  active: true,
};

const EMPTY_SMTP = {
  smtpHost: "",
  smtpPort: 465,
  smtpSecure: true,
  smtpUser: "",
  smtpPass: "",        // "" = not set, "••••••••" = already set, anything else = new pass
  smtpFromEmail: "",
  smtpReplyTo: "",
  smtpEnabled: false,
};

const PASS_MASK = "••••••••";

// ─── ImageUploadField ─────────────────────────────────────────────────────────

const ImageUploadField = ({ label, value, onChange, classes }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) { toast.error("El archivo no puede superar 1MB"); return; }
    if (!["image/png", "image/jpeg", "image/svg+xml", "image/webp"].includes(file.type)) {
      toast.error("Solo se aceptan PNG, JPG, SVG o WEBP"); return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/superadmin/whitelabel/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={classes.fieldSection}>
      <div className={classes.fieldLabel}>{label}</div>
      <div className={classes.uploadSection}>
        {value && <img src={value} alt="preview" className={classes.uploadPreview} />}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          <TextField
            size="small" variant="outlined" fullWidth
            placeholder="URL de la imagen o subir archivo"
            value={value} onChange={(e) => onChange(e.target.value)}
          />
          <label>
            <input
              type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
              style={{ display: "none" }}
              onChange={handleFileChange} disabled={uploading}
            />
            <Button
              size="small" variant="outlined" component="span"
              startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
              disabled={uploading}
            >
              {uploading ? "Subiendo..." : "Subir archivo"}
            </Button>
          </label>
        </div>
      </div>
      <Typography variant="caption" color="textSecondary">Máx 1MB · PNG, JPG, SVG o WEBP</Typography>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const SuperAdminWhiteLabel = () => {
  const classes = useStyles();
  const [configs, setConfigs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  // WL edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // SMTP dialog (feat_095)
  const [smtpTarget, setSmtpTarget] = useState(null);   // WhiteLabelConfig row
  const [smtpForm, setSmtpForm] = useState(EMPTY_SMTP);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpShowPass, setSmtpShowPass] = useState(false);
  const [smtpLastTest, setSmtpLastTest] = useState(null); // { at, success, error }
  // Test sub-dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);   // { success, error? }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wlRes, dashRes] = await Promise.all([
        api.get("/superadmin/whitelabel"),
        api.get("/superadmin/dashboard"),
      ]);
      setConfigs(wlRes.data);
      setCompanies(dashRes.data.companies || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── WL CRUD ───────────────────────────────────────────────────────────────

  const openCreate = () => { setEditTarget(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditTarget(c);
    setForm({
      domain: c.domain || "",
      companyId: c.companyId || "",
      type: c.type || "company",
      name: c.name || "",
      logoUrl: c.logoUrl || "",
      logoUrlDark: c.logoUrlDark || "",
      chatEmptyImageUrl: c.chatEmptyImageUrl || "",
      chatEmptyImageUrlDark: c.chatEmptyImageUrlDark || "",
      loginLogoUrl: c.loginLogoUrl || "",
      active: c.active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.domain || !form.name) { toast.error("Dominio y nombre son obligatorios"); return; }
    if (form.type === "company" && !form.companyId) { toast.error("Seleccioná una empresa para tipo 'Empresa específica'"); return; }
    setSaving(true);
    try {
      if (editTarget) { await api.put(`/superadmin/whitelabel/${editTarget.id}`, form); toast.success("Configuración actualizada"); }
      else { await api.post("/superadmin/whitelabel", form); toast.success("Dominio de marca blanca creado"); }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/superadmin/whitelabel/${deleteTarget.id}`);
      toast.success("Configuración eliminada");
      setDeleteTarget(null);
      fetchAll();
    } catch (err) { toastError(err); }
    finally { setDeleting(false); }
  };

  const sf = (field) => (val) => setForm(f => ({ ...f, [field]: val }));

  // ─── SMTP dialog (feat_095) ────────────────────────────────────────────────

  const openSmtp = async (c) => {
    setSmtpTarget(c);
    setSmtpForm(EMPTY_SMTP);
    setSmtpShowPass(false);
    setTestResult(null);
    setSmtpLastTest(null);
    setSmtpLoading(true);
    try {
      const { data } = await api.get(`/superadmin/whitelabel/${c.id}/smtp`);
      setSmtpForm({
        smtpHost: data.smtpHost || "",
        smtpPort: data.smtpPort || 465,
        smtpSecure: data.smtpSecure !== false,
        smtpUser: data.smtpUser || "",
        smtpPass: data.smtpPassSet ? PASS_MASK : "",
        smtpFromEmail: data.smtpFromEmail || "",
        smtpReplyTo: data.smtpReplyTo || "",
        smtpEnabled: Boolean(data.smtpEnabled),
      });
      setSmtpLastTest(data.smtpLastTestAt ? {
        at: data.smtpLastTestAt,
        success: data.smtpLastTestSuccess,
        error: data.smtpLastTestError,
      } : null);
    } catch (err) { toastError(err); }
    finally { setSmtpLoading(false); }
  };

  const handleSmtpSave = async () => {
    if (!smtpTarget) return;
    setSmtpSaving(true);
    try {
      const payload = { ...smtpForm };
      // If password is unchanged mask, don't send it
      if (payload.smtpPass === PASS_MASK) delete payload.smtpPass;
      await api.put(`/superadmin/whitelabel/${smtpTarget.id}/smtp`, {
        ...payload,
        smtpSecure: smtpForm.smtpSecure,
        smtpEnabled: smtpForm.smtpEnabled,
      });
      toast.success("Configuración SMTP guardada");
      // Refresh last test info
      const { data } = await api.get(`/superadmin/whitelabel/${smtpTarget.id}/smtp`);
      setSmtpLastTest(data.smtpLastTestAt ? {
        at: data.smtpLastTestAt,
        success: data.smtpLastTestSuccess,
        error: data.smtpLastTestError,
      } : null);
    } catch (err) { toastError(err); }
    finally { setSmtpSaving(false); }
  };

  const openTestDialog = () => {
    setTestRecipient("");
    setTestResult(null);
    setTestDialogOpen(true);
  };

  const handleSendTest = async () => {
    if (!smtpTarget || !testRecipient) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/superadmin/whitelabel/${smtpTarget.id}/smtp/test`, {
        testRecipient,
      });
      setTestResult(data);
      // Refresh last test info in parent dialog
      const { data: smtpData } = await api.get(`/superadmin/whitelabel/${smtpTarget.id}/smtp`);
      setSmtpLastTest(smtpData.smtpLastTestAt ? {
        at: smtpData.smtpLastTestAt,
        success: smtpData.smtpLastTestSuccess,
        error: smtpData.smtpLastTestError,
      } : null);
    } catch (err) {
      setTestResult({ success: false, error: err?.response?.data?.error || err.message || "Error desconocido" });
    } finally {
      setTestSending(false);
    }
  };

  const sfSmtp = (field) => (val) => setSmtpForm(f => ({ ...f, [field]: val }));

  const smtpTestAge = () => {
    if (!smtpLastTest?.at) return null;
    const mins = Math.round((Date.now() - new Date(smtpLastTest.at).getTime()) / 60000);
    if (mins < 1) return "hace menos de 1 min";
    if (mins === 1) return "hace 1 min";
    if (mins < 60) return `hace ${mins} min`;
    const hours = Math.round(mins / 60);
    return `hace ${hours}h`;
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5">Marca Blanca</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
          Agregar dominio
        </Button>
      </div>

      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Cada dominio registrado muestra el nombre y logos del cliente en lugar de WAIA.
        El cliente debe configurar un <strong>CNAME</strong> apuntando a <code>waia.zzia.info</code> con SSL proxy (ej: Cloudflare).
      </Typography>

      {loading ? <CircularProgress /> : (
        <Paper className={classes.paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dominio</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Empresa / Instancia</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Logo sidebar</TableCell>
                <TableCell>Logo oscuro</TableCell>
                <TableCell>Logo login</TableCell>
                <TableCell>Chat vacío</TableCell>
                <TableCell>Chat vacío oscuro</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" style={{ opacity: 0.5, padding: 32 }}>
                    No hay dominios configurados
                  </TableCell>
                </TableRow>
              )}
              {configs.map((c) => (
                <TableRow key={c.id}>
                  <TableCell><code style={{ fontSize: 12 }}>{c.domain}</code></TableCell>
                  <TableCell>
                    <Chip
                      label={c.type === "instance" ? "Instancia" : "Empresa"}
                      size="small"
                      color={c.type === "instance" ? "secondary" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {c.type === "company"
                      ? (c.company?.name || c.companyId)
                      : <span style={{ opacity: 0.4 }}>—</span>
                    }
                  </TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.logoUrl ? <img src={c.logoUrl} alt="logo" className={classes.logoPreview} /> : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>
                  <TableCell>{c.logoUrlDark ? <img src={c.logoUrlDark} alt="logo dark" className={classes.logoPreview} style={{ background: "#1a1a1a" }} /> : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>
                  <TableCell>{c.loginLogoUrl ? <img src={c.loginLogoUrl} alt="login" className={classes.logoPreview} /> : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>
                  <TableCell>{c.chatEmptyImageUrl ? <img src={c.chatEmptyImageUrl} alt="empty" className={classes.logoPreview} /> : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>
                  <TableCell>{c.chatEmptyImageUrlDark ? <img src={c.chatEmptyImageUrlDark} alt="empty dark" className={classes.logoPreview} style={{ background: "#1a1a1a" }} /> : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>
                  <TableCell>
                    <Chip label={c.active ? "Activo" : "Inactivo"} size="small" color={c.active ? "primary" : "default"} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Configurar SMTP"><IconButton size="small" onClick={() => openSmtp(c)}><EmailIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Eliminar"><IconButton size="small" onClick={() => setDeleteTarget(c)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ─── WL edit dialog ──────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editTarget ? "Editar configuración" : "Nuevo dominio de marca blanca"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Dominio del cliente *" variant="outlined" fullWidth size="small"
            value={form.domain} onChange={(e) => sf("domain")(e.target.value.toLowerCase())}
            placeholder="crm.micliente.com" style={{ marginBottom: 16 }}
            helperText="Sin https:// ni barra final. Ej: crm.micliente.com"
          />
          <FormControl fullWidth size="small" variant="outlined" style={{ marginBottom: 16 }}>
            <InputLabel>Tipo *</InputLabel>
            <Select
              value={form.type}
              onChange={(e) => setForm(f => ({ ...f, type: e.target.value, companyId: "" }))}
              label="Tipo *"
            >
              <MenuItem value="company">Empresa específica — un dominio para una empresa</MenuItem>
              <MenuItem value="instance">Instancia completa — un dominio para toda una plataforma</MenuItem>
            </Select>
          </FormControl>
          {form.type === "company" && (
            <FormControl fullWidth size="small" variant="outlined" style={{ marginBottom: 16 }}>
              <InputLabel>Empresa asociada *</InputLabel>
              <Select value={form.companyId} onChange={(e) => sf("companyId")(e.target.value)} label="Empresa asociada *">
                <MenuItem value=""><em>Seleccionar empresa</em></MenuItem>
                {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name} (ID {c.id})</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Nombre que reemplaza WAIA *" variant="outlined" fullWidth size="small"
            value={form.name} onChange={(e) => sf("name")(e.target.value)}
            placeholder="MiCRM Pro" style={{ marginBottom: 16 }}
            helperText="Aparece en el AppBar y como título de app"
          />
          <Divider style={{ margin: "8px 0 16px" }} />
          <ImageUploadField label="Logo sidebar / AppBar — tema claro (pequeño, aprox 36px alto)" value={form.logoUrl} onChange={sf("logoUrl")} classes={classes} />
          <ImageUploadField label="Logo sidebar / AppBar — tema oscuro (opcional)" value={form.logoUrlDark} onChange={sf("logoUrlDark")} classes={classes} />
          <ImageUploadField label="Logo login (grande, aprox 300px ancho)" value={form.loginLogoUrl} onChange={sf("loginLogoUrl")} classes={classes} />
          <ImageUploadField label="Imagen chat vacío — tema claro (centrada cuando no hay ticket abierto)" value={form.chatEmptyImageUrl} onChange={sf("chatEmptyImageUrl")} classes={classes} />
          <ImageUploadField label="Imagen chat vacío — tema oscuro (opcional)" value={form.chatEmptyImageUrlDark} onChange={sf("chatEmptyImageUrlDark")} classes={classes} />
          <Divider style={{ margin: "8px 0 16px" }} />
          <FormControlLabel
            control={<Switch checked={form.active} onChange={(e) => sf("active")(e.target.checked)} color="primary" />}
            label="Activo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : editTarget ? "Guardar cambios" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── SMTP dialog (feat_095) ───────────────────────────────────────────── */}
      <Dialog open={Boolean(smtpTarget)} onClose={() => !smtpSaving && setSmtpTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <EmailIcon color="primary" />
            <span>Configuración SMTP — {smtpTarget?.name}</span>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {smtpLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <>
              {/* Enable toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={smtpForm.smtpEnabled}
                    onChange={(e) => sfSmtp("smtpEnabled")(e.target.checked)}
                    color="primary"
                  />
                }
                label={<strong>Habilitar envío de mails desde esta instancia</strong>}
                style={{ marginBottom: 16, display: "block" }}
              />

              <Divider style={{ margin: "0 0 16px" }} />

              {/* Server + port + SSL */}
              <div className={classes.smtpRow}>
                <TextField
                  label="Servidor SMTP"
                  variant="outlined"
                  size="small"
                  style={{ flex: 2 }}
                  value={smtpForm.smtpHost}
                  onChange={(e) => { const v = e.target.value; sfSmtp("smtpHost")(v); }}
                  placeholder="smtp.hostinger.com"
                  disabled={smtpSaving}
                />
                <TextField
                  label="Puerto"
                  variant="outlined"
                  size="small"
                  style={{ flex: 1 }}
                  value={smtpForm.smtpPort}
                  onChange={(e) => { const v = e.target.value; sfSmtp("smtpPort")(v); }}
                  placeholder="465"
                  type="number"
                  inputProps={{ min: 1, max: 65535 }}
                  disabled={smtpSaving}
                />
              </div>

              <FormControlLabel
                control={
                  <Switch
                    checked={smtpForm.smtpSecure}
                    onChange={(e) => sfSmtp("smtpSecure")(e.target.checked)}
                    color="primary"
                    size="small"
                    disabled={smtpSaving}
                  />
                }
                label="SSL/TLS directo (puerto 465) — desactivar para STARTTLS (puerto 587)"
                style={{ marginBottom: 16, display: "block" }}
              />

              {/* User + pass */}
              <TextField
                label="Usuario SMTP"
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginBottom: 16 }}
                value={smtpForm.smtpUser}
                onChange={(e) => { const v = e.target.value; sfSmtp("smtpUser")(v); }}
                placeholder="alertas@noesunbot.com"
                disabled={smtpSaving}
              />

              <TextField
                label="Contraseña SMTP"
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginBottom: 8 }}
                type={smtpShowPass ? "text" : "password"}
                value={smtpForm.smtpPass}
                onChange={(e) => { const v = e.target.value; sfSmtp("smtpPass")(v); }}
                placeholder="Contraseña del servidor SMTP"
                disabled={smtpSaving}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSmtpShowPass(v => !v)}>
                        {smtpShowPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 16 }}>
                La contraseña se guarda cifrada con AES-256-GCM. Dejá el campo como está si no querés cambiarla.
              </Typography>

              {/* From + ReplyTo */}
              <TextField
                label="Mail remitente (From)"
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginBottom: 16 }}
                value={smtpForm.smtpFromEmail}
                onChange={(e) => { const v = e.target.value; sfSmtp("smtpFromEmail")(v); }}
                placeholder="alertas@noesunbot.com"
                helperText="Generalmente igual al usuario SMTP. El nombre de display es el nombre de la marca."
                disabled={smtpSaving}
              />

              <TextField
                label="Reply-To (opcional)"
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginBottom: 16 }}
                value={smtpForm.smtpReplyTo}
                onChange={(e) => { const v = e.target.value; sfSmtp("smtpReplyTo")(v); }}
                placeholder="soporte@micliente.com"
                helperText="Si el destinatario responde el mail, la respuesta irá a esta dirección."
                disabled={smtpSaving}
              />

              {/* Last test status */}
              {smtpLastTest && (
                <>
                  <Divider style={{ margin: "8px 0 12px" }} />
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    {smtpLastTest.success
                      ? <CheckCircleIcon style={{ fontSize: 16, color: "#4caf50" }} />
                      : <ErrorIcon style={{ fontSize: 16, color: "#f44336" }} />
                    }
                    <Typography variant="caption">
                      Último test: {smtpTestAge()} —
                      {smtpLastTest.success
                        ? <span style={{ color: "#4caf50" }}> ✅ OK</span>
                        : <span style={{ color: "#f44336" }}> ❌ {smtpLastTest.error}</span>
                      }
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmtpTarget(null)} disabled={smtpSaving}>
            Cerrar
          </Button>
          <Button
            onClick={openTestDialog}
            variant="outlined"
            color="primary"
            startIcon={<SendIcon />}
            disabled={smtpLoading || smtpSaving || !smtpForm.smtpHost}
          >
            Probar envío
          </Button>
          <Button
            onClick={handleSmtpSave}
            color="primary"
            variant="contained"
            disabled={smtpLoading || smtpSaving}
          >
            {smtpSaving ? <CircularProgress size={18} /> : "Guardar SMTP"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── SMTP test sub-dialog ────────────────────────────────────────────── */}
      <Dialog open={testDialogOpen} onClose={() => !testSending && setTestDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Probar envío SMTP</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Se enviará un mail de prueba usando la configuración guardada en el servidor. Asegurate de guardar los cambios primero.
          </Typography>
          <TextField
            label="Destinatario de prueba"
            variant="outlined"
            size="small"
            fullWidth
            type="email"
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            placeholder="mariano@ejemplo.com"
            autoFocus
            disabled={testSending}
          />
          {testResult && (
            <div className={`${classes.testResult} ${testResult.success ? classes.testOk : classes.testErr}`}>
              {testResult.success
                ? "✅ Mail enviado correctamente. Revisá tu bandeja de entrada."
                : `❌ Error: ${testResult.error}`
              }
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)} disabled={testSending}>Cerrar</Button>
          <Button
            onClick={handleSendTest}
            color="primary"
            variant="contained"
            disabled={testSending || !testRecipient}
            startIcon={testSending ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {testSending ? "Enviando..." : "Enviar test"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete dialog ───────────────────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Eliminar dominio</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar la configuración para <strong>{deleteTarget?.domain}</strong>?
            Los usuarios de ese dominio serán redirigidos a waia.zzia.info.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SuperAdminWhiteLabel;
